import type { AssignedModuleSummary, ProgrammeSummary, SessionContext } from "@/platform";
import type { FinalSignOffCapacity, WorkflowState } from "@/server/types";
import { formatDate } from "@/utils/format-instant";

import { responsibilityLabel, roleLabel } from "./role-labels";

export interface BackendDashboardProgramme {
  id: string;
  title: string;
  status: string;
  /**
   * What the reader is on this item, e.g. `Lead`. One fact, not a sentence,
   * and absent on a module row: the role is held over the programme, which
   * the module row already names.
   */
  yourRole?: string;
  /** The programme a module belongs to. Absent on a programme's own row. */
  programmeTitle?: string;
  /** Who holds the work, when that is someone other than the reader. */
  owner?: string;
  updated: string;
  kind?: string;
  programmeId: string;
  moduleId?: string;
  attentionReason?: string;
}

export interface BackendDashboardSection {
  title: string;
  summary: string;
  /**
   * What the open control on each row does. The section knows and the row's
   * kind does not: one module is built here and decided on two sections up.
   */
  openLabel: string;
  programmes: BackendDashboardProgramme[];
}

export function formatQueueDate(value?: string | null): string {
  return value ? formatDate(value) : "Not recorded";
}

export const LEAD_ATTENTION_STATES = new Set<WorkflowState>([
  "Draft",
  "Ready for Development",
  "In Development",
  "Changes Required",
]);
export const REVIEWER_ATTENTION_STATES = new Set<WorkflowState>([
  "Awaiting Internal Review",
  "Internal Review",
]);
/** Approved content still needs the lead's sign-off and then the export. */
const LEAD_SIGN_OFF_STATES = new Set<WorkflowState>(["Approved", "Final Descriptor Signed Off"]);

/** What the reader holds on the programme, which decides whose move it is. */
export interface AttentionReader {
  reviews?: boolean;
  leads?: boolean;
  admin?: boolean;
}

export function attentionReason(
  workflowState: WorkflowState,
  kind: "Programme" | "Module",
  { reviews = false, leads = true, admin = false }: AttentionReader = {},
  signedOff: readonly FinalSignOffCapacity[] = [],
): string {
  if (kind === "Module") {
    return workflowState === "Changes Required"
      ? "Address the requested changes in this assigned module."
      : "Continue development of this assigned module.";
  }

  switch (workflowState) {
    case "Draft":
      return "Complete the initial programme setup requirements.";
    case "Ready for Development":
      return "Start module development and confirm the programme team.";
    case "In Development":
      return "Continue development and resolve outstanding readiness items.";
    case "Awaiting Internal Review":
    case "Internal Review":
      return reviews
        ? "Complete the assigned internal review."
        : "Waiting for the reviewers to finish the internal review.";
    case "Changes Required":
      return leads
        ? "Address the changes requested during review."
        : "Waiting for the lead to address the requested changes.";
    case "Awaiting QA Approval":
      return "Complete the approval decision.";
    case "Approved": {
      if (leads && signedOff.includes("lead") && signedOff.includes("qa")) {
        return "Complete the final descriptor sign-off.";
      }
      const leadOwes = leads && !signedOff.includes("lead");
      const qaOwes = admin && !signedOff.includes("qa");
      if (leadOwes && qaOwes) {
        return "Record the lead and QA sign-offs on the final descriptor.";
      }
      if (leadOwes) {
        return "Sign off the final descriptor as programme lead.";
      }
      return qaOwes || !leads
        ? "Record the QA sign-off on the final descriptor."
        : "Waiting for the QA sign-off on the final descriptor.";
    }
    case "Final Descriptor Signed Off":
      return leads
        ? "Export and lock the signed-off programme."
        : "Waiting for the lead to export and lock the programme.";
    default:
      return "Open this item to review the next required action.";
  }
}

export function programmeNeedsAttention(
  session: SessionContext,
  programme: ProgrammeSummary,
): boolean {
  const assignments = session.subject.responsibilities.filter(
    (assignment) => assignment.programmeId === programme.id,
  );
  const hasResponsibility = (responsibility: (typeof assignments)[number]["responsibility"]) =>
    assignments.some((assignment) => assignment.responsibility === responsibility);

  // At Approved each signer is asked only until their own sign-off is recorded,
  // and the lead again once both are, to complete it.
  const signed = programme.finalSignOffCapacities ?? [];
  const approved = programme.workflowState === "Approved";
  const leadWaits = approved && signed.includes("lead") && !signed.includes("qa");
  if (
    session.subject.accountRole === "client-admin" &&
    (programme.workflowState === "Awaiting Internal Review" ||
      programme.workflowState === "Internal Review" ||
      programme.workflowState === "Changes Required" ||
      programme.workflowState === "Awaiting QA Approval" ||
      (approved && !signed.includes("qa")))
  ) {
    return true;
  }

  return (
    (hasResponsibility("lead") &&
      (LEAD_ATTENTION_STATES.has(programme.workflowState) ||
        (LEAD_SIGN_OFF_STATES.has(programme.workflowState) && !leadWaits))) ||
    (hasResponsibility("reviewer") && REVIEWER_ATTENTION_STATES.has(programme.workflowState))
  );
}

export function roleProgrammeQueue(
  session: SessionContext,
  programmes: ProgrammeSummary[],
): ProgrammeSummary[] {
  const role = session.subject.accountRole;
  if (role === "client-admin" || role === "platform-admin") {
    return programmes;
  }

  const programmeIds = new Set(
    session.subject.responsibilities.map((assignment) => assignment.programmeId),
  );
  return programmes.filter((programme) => programmeIds.has(programme.id));
}

export function programmeQueueDetail(session: SessionContext, programmeId: string): string {
  const responsibilities = session.subject.responsibilities
    .filter((assignment) => assignment.programmeId === programmeId)
    .map((assignment) => responsibilityLabel(assignment.responsibility));

  return responsibilities.length
    ? responsibilities.join(", ")
    : `${roleLabel(session.subject.accountRole)} access`;
}

function holds(
  session: SessionContext,
  programmeId: string,
  responsibility: SessionContext["subject"]["responsibilities"][number]["responsibility"],
): boolean {
  return session.subject.responsibilities.some(
    (assignment) =>
      assignment.programmeId === programmeId && assignment.responsibility === responsibility,
  );
}

export function backendProgrammeQueueItems(
  session: SessionContext,
  programmes: ProgrammeSummary[],
): BackendDashboardProgramme[] {
  return roleProgrammeQueue(session, programmes)
    .filter((programme) => programmeNeedsAttention(session, programme))
    .map((programme) => ({
      id: programme.id,
      programmeId: programme.id,
      title: programme.title,
      status: programme.workflowState,
      yourRole: programmeQueueDetail(session, programme.id),
      updated: formatQueueDate(programme.updatedAt),
      kind: "Programme",
      attentionReason: attentionReason(
        programme.workflowState,
        "Programme",
        {
          reviews: holds(session, programme.id, "reviewer"),
          leads: holds(session, programme.id, "lead"),
          admin: session.subject.accountRole === "client-admin",
        },
        programme.finalSignOffCapacities,
      ),
    }));
}

export function moduleAttentionReason(module: AssignedModuleSummary): string {
  switch (module.moduleReviewStatus) {
    case "Changes Requested":
      return module.reviewNote
        ? `Address the programme lead's feedback: ${module.reviewNote}`
        : "Address the changes requested by the programme lead.";
    case "Submitted for Review":
      return "Submitted to the programme lead and awaiting a decision.";
    case "Approved":
      return "Approved by the programme lead.";
    default:
      return "Continue development, then submit this module for review.";
  }
}

export function backendModuleQueueItems(
  modules: AssignedModuleSummary[],
  statuses: Set<AssignedModuleSummary["moduleReviewStatus"]>,
): BackendDashboardProgramme[] {
  return modules
    .filter((module) => statuses.has(module.moduleReviewStatus))
    .map((module) => ({
      id: `${module.programmeId}-${module.moduleId}`,
      programmeId: module.programmeId,
      moduleId: module.moduleId,
      title: module.moduleTitle,
      status: module.moduleReviewStatus,
      // Two facts, not one string. Concatenated they arrived under the single
      // "your role" mark and read as one unlabelled data point, with the
      // programme's name run into the builder's.
      programmeTitle: module.programmeTitle,
      owner: module.ownerName ?? "No builder assigned",
      updated: formatQueueDate(module.updatedAt),
      kind: "Module",
      attentionReason: moduleAttentionReason(module),
    }));
}

/** The reader's own programme work, listed whether or not they lead anything. */
const PROGRAMME_ACTIONS = {
  title: "Required programme actions",
  summary: "Programme decisions and development work assigned to your role.",
  openLabel: "Open programme",
};

export function getBackendDashboardSections(
  session: SessionContext,
  programmes: ProgrammeSummary[],
  modules: AssignedModuleSummary[],
): BackendDashboardSection[] {
  const programmeItems = backendProgrammeQueueItems(session, programmes);
  const builderProgrammeIds = new Set(
    session.subject.responsibilities
      .filter(
        (assignment) =>
          assignment.responsibility === "builder" || assignment.responsibility === "lead",
      )
      .map((assignment) => assignment.programmeId),
  );
  const builderModules = modules.filter(
    (module) =>
      module.ownerUserId === session.user.id && builderProgrammeIds.has(module.programmeId),
  );
  const builderActions = backendModuleQueueItems(
    builderModules,
    new Set(["Draft", "Changes Requested"]),
  );
  const builderUpdates = backendModuleQueueItems(
    builderModules,
    new Set(["Submitted for Review", "Approved"]),
  );
  // Someone else's submission, not the reader's own. A lead who built and
  // submitted a module in their own programme matched this queue and their own
  // "Module review updates" at once, so the same module rendered twice under
  // one `attention-item-<id>`.
  // Scoped to the programmes this reader leads, as `moduleReviewRows` beside
  // it is: a lead on one programme and a builder on another was being asked
  // for a decision on the second, which the server then refuses.
  const ledProgrammeIds = new Set(
    session.subject.responsibilities
      .filter((assignment) => assignment.responsibility === "lead")
      .map((assignment) => assignment.programmeId),
  );
  // A client admin may decide any module's review too, as `programme:review`
  // and the workspace's own review controls both allow. A platform admin may not.
  const administers = session.subject.accountRole === "client-admin";
  const leadReviewItems = backendModuleQueueItems(
    modules.filter(
      (module) =>
        module.ownerUserId !== session.user.id &&
        (administers || ledProgrammeIds.has(module.programmeId)),
    ),
    new Set(["Submitted for Review"]),
  );
  const hasLeadResponsibility = administers || ledProgrammeIds.size > 0;
  const sections: BackendDashboardSection[] = [];

  if (builderModules.length) {
    sections.push(
      {
        title: "Module development",
        summary: "Modules assigned to you that still need development work.",
        openLabel: "Open module",
        programmes: builderActions,
      },
      {
        title: "Module review updates",
        summary: "Modules waiting for a decision or already approved.",
        openLabel: "Open module",
        programmes: builderUpdates,
      },
    );
  }

  if (hasLeadResponsibility) {
    sections.unshift(
      {
        title: "Module review requests",
        summary: "Submitted modules waiting for your review decision.",
        openLabel: "Open module review",
        programmes: leadReviewItems,
      },
      { ...PROGRAMME_ACTIONS, programmes: programmeItems },
    );
  }

  // Building a module takes nothing away from the decisions an account's other
  // roles still owe; with nothing else to show, the section stands alone.
  if (!hasLeadResponsibility && (programmeItems.length > 0 || !sections.length)) {
    sections.unshift({ ...PROGRAMME_ACTIONS, programmes: programmeItems });
  }

  return sections;
}

export const WORKFLOW_DISPLAY_ORDER: readonly WorkflowState[] = [
  "Draft",
  "Ready for Development",
  "In Development",
  "Awaiting Internal Review",
  "Internal Review",
  "Changes Required",
  "Awaiting QA Approval",
  "Approved",
  "Final Descriptor Signed Off",
  "Exported and Locked",
  "Archived",
];
